
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import ShareDialogContent from './ShareDialogContent';

interface ShareNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  note: {
    id: string;
    is_public?: boolean;
    share_id?: string;
    has_private_share?: boolean;
    has_public_share?: boolean;
  };
}

const ShareNoteDialog: React.FC<ShareNoteDialogProps> = ({ isOpen, onClose, note }) => {
  const [isPublic, setIsPublic] = useState(note.is_public || false);
  const [shareableLink, setShareableLink] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [existingPublicLink, setExistingPublicLink] = useState<string>('');
  const [existingPrivateLink, setExistingPrivateLink] = useState<string>('');
  const { toast } = useToast();

  const generateShareableLink = (shareId: string) => {
    return `https://workspace.mindnote.online/share/${shareId}`;
  };

  // Fetch existing share links when dialog opens
  useEffect(() => {
    const fetchExistingLinks = async () => {
      if (!isOpen) return;

      const { data, error } = await supabase
        .from('notes')
        .select('share_id, is_public')
        .eq('id', note.id)
        .single();

      if (!error && data) {
        const shareableLink = generateShareableLink(data.share_id);
        if (data.is_public) {
          setExistingPublicLink(shareableLink);
        } else {
          setExistingPrivateLink(shareableLink);
        }
      }
    };

    fetchExistingLinks();
  }, [isOpen, note.id]);

  // Clear the new link when dialog opens/closes or when switching modes
  useEffect(() => {
    setShareableLink('');
  }, [isOpen, isPublic]);

  const handleUpdateShareSettings = async () => {
    try {
      setIsGeneratingLink(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if sharing mode is already used
      if (isPublic && note.has_public_share) {
        toast({
          title: "Share link already exists",
          description: "This note already has a public share link.",
          variant: "destructive",
        });
        return;
      }
      if (!isPublic && note.has_private_share) {
        toast({
          title: "Share link already exists",
          description: "This note already has a private share link.",
          variant: "destructive",
        });
        return;
      }

      // Get existing share ID or generate new one
      const { data: noteData, error: noteError } = await supabase
        .from('notes')
        .select('share_id')
        .eq('id', note.id)
        .single();

      if (noteError) throw noteError;

      const updates = {
        is_public: isPublic,
        collaborative_editing: isPublic,
        shared_by: user.id,
        has_private_share: !isPublic ? true : note.has_private_share,
        has_public_share: isPublic ? true : note.has_public_share,
        shared_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('notes')
        .update(updates)
        .eq('id', note.id);

      if (error) throw error;

      const universalLink = generateShareableLink(noteData.share_id);
      setShareableLink(universalLink);
      
      // Update the existing links state
      if (isPublic) {
        setExistingPublicLink(universalLink);
      } else {
        setExistingPrivateLink(universalLink);
      }

      toast({
        title: "Share settings updated",
        description: `Note is now ${isPublic ? 'publicly editable' : 'private read-only'}.`,
      });
    } catch (error) {
      console.error('Error updating share settings:', error);
      toast({
        title: "Error updating share settings",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLink(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <ShareDialogContent
          isPublic={isPublic}
          setIsPublic={setIsPublic}
          shareableLink={shareableLink}
          onGenerateLink={handleUpdateShareSettings}
          isGeneratingLink={isGeneratingLink}
          hasPublicShare={note.has_public_share}
          hasPrivateShare={note.has_private_share}
          existingPublicLink={existingPublicLink}
          existingPrivateLink={existingPrivateLink}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ShareNoteDialog;
